import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Play } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, assetUrl } from "@/lib/api";
import { applySeo } from "@/lib/utils";
import { ytThumb, ytEmbed } from "@/lib/youtube";
import { MaskWords, Pill } from "@/components/site/primitives";
import { Reveal } from "@/components/site/Reveal";
import { scrollToId } from "@/hooks/useSmoothScroll";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const L = t.library;
  const [course, setCourse] = useState(undefined);
  const [activeVideo, setActiveVideo] = useState(null); // { id, url } — null = intro

  useEffect(() => {
    setCourse(undefined);
    api.get(`/courses/${slug}`).then((r) => setCourse(r.data)).catch(() => setCourse(null));
  }, [slug]);

  useEffect(() => {
    if (!course) return;
    const desired = (lang === "ro" ? course.slug_ro : course.slug_en) || course.slug;
    if (desired && desired !== slug) navigate(`/library/${desired}`, { replace: true });
  }, [lang, course, slug, navigate]);

  useEffect(() => {
    if (!course) return;
    const en = `/library/${course.slug_en || course.slug}`;
    const ro = `/library/${course.slug_ro || course.slug}`;
    applySeo({
      title: course[`seo_title_${lang}`] || `${g("title")} — Oana Mudura`,
      description: course[`meta_description_${lang}`] || g("description"),
      path: window.location.pathname,
      enPath: en,
      roPath: ro,
    });
  }, [course, lang]);

  const g = (k) => (course ? course[`${k}_${lang}`] || course[`${k}_ro`] || "" : "");
  const lessons = course && Array.isArray(course.lessons) ? course.lessons : [];
  const lt = (l) => (l ? l[`title_${lang}`] || l.title_ro || l.title_en || "" : "");
  const isFilled = (l) => !!(l && (lt(l) || l.youtube_url));

  if (course === undefined)
    return <div className="bg-paper text-ink min-h-screen px-6 pt-48 eyebrow text-ink/40">{L.loadingCourse}</div>;
  if (course === null)
    return (
      <div className="bg-paper text-ink min-h-screen px-6 md:px-12 lg:px-20 pt-40 md:pt-52 pb-32">
        <Link to="/library" data-testid="course-back" data-cursor="link" className="eyebrow inline-flex items-center gap-2 text-ink/50 hover:text-ink transition-colors mb-10">
          <ArrowLeft size={14} /> {L.back}
        </Link>
        <div className="serif text-4xl md:text-5xl">{L.soon}</div>
      </div>
    );

  const cover = assetUrl(course.cover_image) || ytThumb(course.youtube_url);
  const thumbOf = (l) => assetUrl(l.thumbnail) || ytThumb(l.youtube_url) || cover;
  const manifesto = String(g("body")).split("\n").filter((p) => p.trim());
  // Inline multi-player: each lesson becomes its own player, in place.
  // Only one lesson is active at a time (activeVideo holds its id); clicking
  // another lesson flips isActive so the previous one reverts to its thumbnail.
  const playLesson = (l, i) => {
    if (!l.youtube_url) return;
    setActiveVideo({ id: `lesson-${i}`, url: l.youtube_url });
  };

  return (
    <div className="bg-paper text-ink min-h-screen" data-testid="course-detail">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 pt-36 md:pt-48 pb-10 md:pb-14">
        <Link to="/library" data-testid="course-back" data-cursor="link" className="eyebrow inline-flex items-center gap-2 text-ink/50 hover:text-ink transition-colors mb-10">
          <ArrowLeft size={14} /> {L.back}
        </Link>
        {g("subtitle") && <div className="eyebrow text-ink/45 mb-5">{g("subtitle")}</div>}
        <MaskWords
          Tag="h1"
          text={g("title")}
          className="display uppercase text-[13vw] md:text-[10vw] lg:text-[8.5vw] leading-[0.85] break-words"
        />
        {g("description") && (
          <p className="mt-8 serif text-2xl md:text-3xl leading-[1.28] text-ink/80 max-w-3xl">{g("description")}</p>
        )}
      </section>

      {/* Intro video — massive hero player (uses the course's Link YouTube intro) */}
      {course.youtube_url && (
        <section className="px-6 md:px-12 lg:px-20 pb-16 md:pb-24" data-testid="intro-section">
          {activeVideo?.id === "intro" ? (
            <div className="relative overflow-hidden aspect-video bg-black w-full">
              <iframe
                src={ytEmbed(course.youtube_url, true)}
                title={g("title")}
                data-testid="intro-player"
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setActiveVideo({ id: "intro", url: course.youtube_url })}
              data-testid="intro-video"
              data-cursor="link"
              className="group relative block w-full overflow-hidden aspect-video bg-ink/5"
            >
              {cover && (
                <img
                  src={cover}
                  alt={g("title")}
                  onError={(e) => { if (e.currentTarget.src.includes("maxresdefault")) e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "mqdefault"); }}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-white/60 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-500 group-hover:bg-white group-hover:text-black">
                  <Play size={26} className="ml-1" fill="currentColor" />
                </span>
              </span>
            </button>
          )}
        </section>
      )}

      {/* Manifesto */}
      {manifesto.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 py-20 md:py-32">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 lg:col-span-3">
              <div className="eyebrow text-ink/45 lg:sticky lg:top-32">{g("manifesto_label") || L.manifestoLabel}</div>
            </div>
            <div className="col-span-12 lg:col-span-8 space-y-8">
              {manifesto.map((p, i) => (
                <Reveal key={i}>
                  <p className={`serif leading-[1.4] ${i === 0 ? "text-3xl md:text-5xl text-ink" : "text-xl md:text-2xl text-ink/75"}`}>{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Curriculum — dynamic list, unlimited lessons */}
      <section className="px-6 md:px-12 lg:px-20 pb-28 md:pb-40 border-t border-ink/12 pt-16 md:pt-24">
        <div className="flex items-baseline justify-between mb-12 md:mb-20">
          <div className="eyebrow text-ink/45">{L.curriculumLabel}</div>
          <div className="eyebrow text-ink/30 tnum">{lessons.filter(isFilled).length} {lang === "ro" ? "lecții" : "lessons"}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-12 gap-y-14 md:gap-y-24">
          {lessons.map((lesson, i) => {
            const filled = isFilled(lesson);
            const num = String(i + 1).padStart(2, "0");
            const thumb = filled ? thumbOf(lesson) : null;
            const isActive = activeVideo?.id === `lesson-${i}`;

            if (!filled) {
              return (
                <div key={i} data-testid={`lesson-slot-${i}`} className="opacity-40 select-none">
                  <div className="font-display font-extralight text-6xl md:text-7xl text-ink/25 tnum tracking-tighter leading-none mb-5">{num}</div>
                  <div className="aspect-video bg-ink/[0.04] border border-ink/10 mb-4 flex items-center justify-center">
                    <span className="eyebrow text-ink/30">{L.soon}</span>
                  </div>
                  <h3 className="serif text-xl md:text-2xl leading-[1.1] text-ink/40">{lt(lesson) || L.soon}</h3>
                </div>
              );
            }

            if (isActive) {
              return (
                <div key={i} data-testid={`lesson-slot-${i}`} data-active="true" className="w-full">
                  <div className="font-display font-extralight text-6xl md:text-7xl tnum tracking-tighter leading-none mb-5 text-ink">{num}</div>
                  <div className="relative overflow-hidden aspect-video bg-black mb-4 ring-2 ring-ink ring-offset-2 ring-offset-paper">
                    <iframe
                      key={`lesson-${i}`}
                      src={ytEmbed(lesson.youtube_url, true)}
                      title={lt(lesson)}
                      data-testid={`lesson-player-${i}`}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <h3 className="serif text-xl md:text-2xl leading-[1.1] text-ink">{lt(lesson)}</h3>
                </div>
              );
            }

            return (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <button
                  type="button"
                  onClick={() => playLesson(lesson, i)}
                  data-testid={`lesson-slot-${i}`}
                  data-active="false"
                  data-cursor="link"
                  className="group text-left w-full"
                >
                  <div className="font-display font-extralight text-6xl md:text-7xl tnum tracking-tighter leading-none mb-5 transition-colors duration-500 text-ink/25 group-hover:text-ink">
                    {num}
                  </div>
                  <div className="relative overflow-hidden aspect-video bg-ink/5 mb-4">
                    {thumb && (
                      <img
                        src={thumb}
                        alt={lt(lesson)}
                        onError={(e) => { if (e.currentTarget.src.includes("maxresdefault")) e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "mqdefault"); }}
                        className="duo w-full h-full object-contain"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                    {lesson.youtube_url && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full border border-white/60 text-white flex items-center justify-center backdrop-blur-sm transition-all duration-500 group-hover:bg-white group-hover:text-black">
                          <Play size={17} className="ml-0.5" fill="currentColor" />
                        </span>
                      </span>
                    )}
                  </div>
                  <h3 className="serif text-xl md:text-2xl leading-[1.1] transition-transform duration-500 group-hover:translate-x-1">{lt(lesson)}</h3>
                </button>
              </Reveal>
            );
          })}
          {lessons.length === 0 && (
            <div className="col-span-full eyebrow text-ink/40">{L.soon}</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-noir text-paper px-6 md:px-12 lg:px-20 py-24 md:py-36">
        <Reveal>
          <p className="serif italic text-3xl md:text-5xl leading-[1.1] max-w-3xl mb-12 break-words">{t.contact.big}</p>
          <Pill dark onClick={() => scrollToId("#contact")}>{t.contact.dialogueLabel}</Pill>
        </Reveal>
      </section>
    </div>
  );
}
